import { Building2 as CardIconBuilding2, Package as CardIconPackage } from 'lucide-react';
import { CardHeader as ContentCardHeader } from '@hue/design-system';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, FormErrorSummary, Select, useSnackbar } from '@hue/design-system';
import { prepaidPolicySchema, type PrepaidSettings, type PrepaidOverrides } from '@hue/contracts';
import { getApiError } from '../../lib/api.js';
import { ErrorCard, LoadingCard } from '../../app/data-view.js';
import { prepaidApi } from './api.js';
import { PolicyEditor } from './policy-editor.js';
import { PageHeader } from '../../app/page-title.js';

export function PrepaidSettingsScreen({
  units,
}: {
  units: Array<{ id: string; displayName: string }>;
}) {
  const query = useQuery({
    queryKey: ['company', 'prepaid', 'settings'],
    queryFn: prepaidApi.settings,
    retry: false,
  });
  return query.isLoading ? (
    <LoadingCard label="Carregando regras dos saldos" />
  ) : !query.data ? (
    <ErrorCard message={getApiError(query.error)} />
  ) : (
    <SettingsForm key={query.dataUpdatedAt} initial={query.data} units={units} />
  );
}
function SettingsForm({
  initial,
  units,
}: {
  initial: PrepaidSettings;
  units: Array<{ id: string; displayName: string }>;
}) {
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const client = useQueryClient();
  const snackbar = useSnackbar();
  async function save() {
    setBusy(true);
    setError(undefined);
    try {
      await prepaidApi.saveSettings(value);
      await client.invalidateQueries({ queryKey: ['company', 'prepaid'] });
      snackbar.success(
        'Regras salvas. Mudanças valem para os próximos saldos; uso em outra unidade vale imediatamente.',
      );
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section>
      <PageHeader
        trail="Configurações / Saldos pré-pagos"
        title="Saldos pré-pagos"
        description="Regras da empresa, com ajustes por unidade e por pacote. Cada saldo preserva as regras da venda."
      />
      <div className="grid gap-6">
        <form
          id="prepaid-settings"
          className="grid gap-6"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <section
            aria-label="Padrão da empresa"
            className="grid gap-4 rounded-2xl border border-surface-border bg-surface-card p-5"
          >
            <ContentCardHeader
              title="Padrão da empresa"
              icon={<CardIconBuilding2 className="size-4" />}
            />
            <PolicyEditor
              value={value.company}
              onChange={(company) =>
                setValue({ ...value, company: prepaidPolicySchema.parse(company) })
              }
            />
            <Select
              label="Uso em outra unidade"
              value={value.crossUnit}
              options={[
                { value: 'SALE_UNIT', label: 'Só na unidade da venda' },
                { value: 'ANY_UNIT', label: 'Em qualquer unidade' },
              ]}
              onValueChange={(crossUnit) =>
                setValue({ ...value, crossUnit: crossUnit as PrepaidSettings['crossUnit'] })
              }
            />
          </section>
          {units.map((unit) => (
            <section
              key={unit.id}
              aria-label={`Regras de ${unit.displayName}`}
              className="grid gap-4 rounded-2xl border border-surface-border bg-surface-card p-5"
            >
              <ContentCardHeader
                title={`Regras de ${unit.displayName}`}
                icon={<CardIconBuilding2 className="size-4" />}
              />
              <PolicyEditor
                value={value.units[unit.id] ?? {}}
                inherited={value.company}
                onChange={(rules) =>
                  setValue({ ...value, units: { ...value.units, [unit.id]: rules } })
                }
              />
            </section>
          ))}
          {error ? (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={busy}>
              Salvar regras
            </Button>
            <FormErrorSummary form="prepaid-settings" />
          </div>
        </form>
      </div>
    </section>
  );
}

export function PackagePrepaidRules({ packageId }: { packageId: string }) {
  const settings = useQuery({
    queryKey: ['company', 'prepaid', 'settings'],
    queryFn: prepaidApi.settings,
    retry: false,
  });
  const rules = useQuery({
    queryKey: ['company', 'prepaid', 'rules', packageId],
    queryFn: () => prepaidApi.rules(packageId),
    retry: false,
  });
  if (settings.isLoading || rules.isLoading)
    return <LoadingCard label="Carregando regras do pacote" />;
  if (!settings.data || !rules.data)
    return <ErrorCard message={getApiError(settings.error ?? rules.error)} />;
  return (
    <PackageRulesForm
      key={`${packageId}:${rules.dataUpdatedAt}`}
      packageId={packageId}
      initial={rules.data}
      settings={settings.data}
    />
  );
}
function PackageRulesForm({
  packageId,
  initial,
  settings,
}: {
  packageId: string;
  initial: PrepaidOverrides;
  settings: PrepaidSettings;
}) {
  const [rules, setRules] = useState(initial);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const client = useQueryClient();
  const snackbar = useSnackbar();
  async function save() {
    setBusy(true);
    setError(undefined);
    try {
      await prepaidApi.saveRules(packageId, rules);
      await client.invalidateQueries({ queryKey: ['company', 'prepaid', 'rules', packageId] });
      snackbar.success('Regras do pacote salvas para as próximas vendas.');
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form
      id="prepaid-package-rules"
      className="grid gap-4 rounded-2xl border border-surface-border bg-surface-card p-5"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <ContentCardHeader
        title="Regras dos saldos deste pacote"
        icon={<CardIconPackage className="size-4" />}
      />
      <p className="text-sm text-content-muted">
        Herdar mantém a regra da unidade da venda. O valor mostrado abaixo é o padrão da empresa; a
        unidade pode ajustá-lo.
      </p>
      <PolicyEditor value={rules} inherited={settings.company} onChange={setRules} />
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-3">
        <Button type="submit" disabled={busy}>
          Salvar regras do pacote
        </Button>
        <FormErrorSummary form="prepaid-package-rules" />
      </div>
    </form>
  );
}
